import {
  GridLight,
  InfoLight,
  PlayLight,
  SensorsLightCustom,
  SignOutCircleLight,
  SlantPilotCustom,
  StopLight,
} from '@repo/go-icons';
import { ActionsBar, Tooltip } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { DoubleTooltipSidebarItem } from '../../../shared/components/DoubleTooltipSidebarItem/DoubleTooltipSidebarItem';
import { SelectableWellboreList } from '../../../shared/components/SelectableWellboreList/SelectableWellboreList';
import { TimezoneSwitch } from '../../../shared/components/TimezoneSwitch/TimezoneSwitch';
import type { VideoBroadcastWidgetState } from '../../store/VideoBroadcastWidgetState';
import { VideoTooltipContext } from '../../VideoTooltipContext';

import { AdditionalInfo } from './SidebarActions/AdditionalInfo';
import { CamerasList } from './SidebarActions/CamerasList';
import { ImageAspectRatio } from './SidebarActions/ImageAspectRatio';

export const SidebarVideo = observer(function SidebarVideo({
  widgetState,
}: {
  widgetState: VideoBroadcastWidgetState;
}) {
  const rootContainer = useContext(VideoTooltipContext);
  const { t } = useTranslation('videoBroadcastWidget', { useSuspense: false });
  const showHint = !widgetState.anyCameraSelected && !!widgetState.cameras.length && !widgetState.isCameraListOpen;

  return (
    <ActionsBar>
      <ActionsBar.TopBar>
        <DoubleTooltipSidebarItem
          outerTitle={t('sidebar.additionalInfo')}
          innerTitle={() => <AdditionalInfo widgetState={widgetState} />}
          icon={<InfoLight />}
        />

        <DoubleTooltipSidebarItem
          outerTitle={t('sidebar.camerasList')}
          innerTitle={() => <CamerasList widgetState={widgetState} />}
          icon={<SensorsLightCustom />}
          disabled={widgetState.cameras.length < 1}
          handleClick={(value) => widgetState.setIsCameraListOpen(value)}
          hintProps={{
            rootContainer,
            open: showHint,
            description: t('sidebar.selectCameraHint'),
            placement: 'left',
          }}
        />

        <Tooltip trigger={['hover']} placement="left" title={t('sidebar.playStop')}>
          <ActionsBar.Item onClick={() => widgetState.togglePlayAll()}>
            {widgetState.playAll ? <StopLight /> : <PlayLight />}
          </ActionsBar.Item>
        </Tooltip>

        <DoubleTooltipSidebarItem
          outerTitle={t('sidebar.proportions')}
          innerTitle={() => <ImageAspectRatio widgetState={widgetState} />}
          icon={<GridLight />}
        />

        {!widgetState.wellboresLoading && (
          <DoubleTooltipSidebarItem
            outerTitle={t('sidebar.wellboreSelection')}
            innerTitle={() => (
              <SelectableWellboreList
                wellbores={widgetState.wellbores.list}
                onChange={(id) => widgetState.switchWellbore(id)}
                value={widgetState.wellbores.selected?.id}
                activeTitle={t('sidebar.activeWellbore')}
              />
            )}
            icon={<SlantPilotCustom />}
          />
        )}
      </ActionsBar.TopBar>
      <ActionsBar.BottomBar>
        {widgetState.videoInterval != null && (
          <Tooltip trigger={['hover']} placement="left" title={t('sidebar.exitVideoInterval')}>
            <ActionsBar.Item
              active={widgetState.videoInterval != null}
              onClick={() => widgetState.clearVideoInterval()}
            >
              <SignOutCircleLight />
            </ActionsBar.Item>
          </Tooltip>
        )}
        {widgetState.timeZonesStore.wellTimeZone != null && (
          <>
            <ActionsBar.Divider />
            <TimezoneSwitch timeZones={widgetState.timeZonesStore} />
          </>
        )}
      </ActionsBar.BottomBar>
    </ActionsBar>
  );
});
